import { endOfMonth, getWeeksInMonth } from "@internationalized/date";
import { useCalendarGrid } from "@react-aria/calendar";
import { useLocale } from "@react-aria/i18n";
import { CalendarCell } from "./CalendarCell";
import Table from "@splitfire-agency/raiden-library/dist/components/Table";
import { Text } from "@chakra-ui/react";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import adjustDateWithTime from "@raiden/library/helpers/datePicker/adjustDateWithTime";

dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

/**
 * @typedef Props
 * @property {import("@raiden/library/types/RentalArea").RentalArea} rentalArea
 * @property {import("@raiden/library/types/common/DatePicker").UnavailableRanges[]} unavailableRanges
 * @property {import("@react-stately/calendar").CalendarState} state
 * @property {object} [offset]
 * @property {() => void} [onClick]
 */
/**
 * @param {Props} props
 */
export function CalendarGrid({
  rentalArea,
  unavailableRanges,
  state,
  offset = {},
  onClick,
}) {
  let { locale } = useLocale();

  let startDate = state.visibleRange.start.add(offset);

  let endDate = endOfMonth(startDate);

  let { gridProps, headerProps, weekDays } = useCalendarGrid(
    {
      startDate,
      endDate,
    },
    state,
  );

  // Get the number of weeks in the month so we can render the proper number of rows.
  let weeksInMonth = Math.max(
    getWeeksInMonth(state.visibleRange.start, locale),
    getWeeksInMonth(state.visibleRange.end, locale),
  );

  /**
   * @param {object} param0
   * @param {import("@internationalized/date").CalendarDate} param0.date
   * @param {import("@raiden/library/types/common/DatePicker").Schedule} [param0.schedule]
   *
   * @returns {import("@raiden/library/types/common/DatePicker").UnavailableRanges[]}
   */
  function getUnavailableRangesForOneDate({ date, schedule }) {
    let unavailableRangesForDay = [];

    const currentDate = dayjs(date.toDate("Europe/Paris"));
    const scheduleStart = schedule?.start_at;
    const scheduleEnd = schedule?.end_at;

    unavailableRanges.forEach((range) => {
      if (isRangeUnavailable(scheduleStart, scheduleEnd, range, currentDate)) {
        unavailableRangesForDay.push(range);
      }
    });
    return unavailableRangesForDay;
  }

  // Vérifie si la plage horaire est indisponible
  function isRangeUnavailable(scheduleStart, scheduleEnd, range, currentDate) {
    // Récupère la date de début et de fin avec l'heure de début et de fin
    const scheduleStartDateTime = adjustDateWithTime({
      date: currentDate,
      time: scheduleStart ?? "00:00:00:00",
    });
    const scheduleEndDateTime = adjustDateWithTime({
      date: currentDate,
      time: scheduleEnd ?? "23:59:59:999",
    });

    // Vérifie si la plage horaire est incluse dans la période d'indisponibilité
    const isWithinOrCoveringDay =
      scheduleStartDateTime.isSameOrAfter(range.from, "minute") &&
      scheduleEndDateTime.isSameOrBefore(range.to, "minute");

    // Vérifie si la date est incluse dans la période d'indisponibilité
    const isDateInRangeOrEqual =
      currentDate.isBetween(range.from, range.to, "minute", "[]") ||
      currentDate.isSame(dayjs(range.from), "day") ||
      currentDate.isSame(dayjs(range.to), "day");

    return isWithinOrCoveringDay || isDateInRangeOrEqual;
  }

  return (
    <Table {...gridProps}>
      <Table.Thead {...headerProps}>
        <Table.Tr>
          {weekDays.map((day, index) => (
            <Table.Th key={index} px="0" pb="5px" borderColor="gray.200">
              <Text align="center">{day}</Text>
            </Table.Th>
          ))}
        </Table.Tr>
      </Table.Thead>

      <Table.Tbody>
        {[...new Array(weeksInMonth).keys()].map((weekIndex) => (
          <Table.Tr key={weekIndex}>
            {state.getDatesInWeek(weekIndex, startDate).map((date, i) => {
              if (date) {
                return (
                  <CalendarCell
                    key={i}
                    unavailableRanges={getUnavailableRangesForOneDate({
                      date,
                      schedule: {
                        start_at: rentalArea.opening_hour,
                        end_at: rentalArea.closing_hour,
                      },
                    })}
                    state={state}
                    date={date}
                    isBookableOnline={rentalArea.is_bookable_online ?? false}
                    opening_hour={rentalArea.opening_hour}
                    closing_hour={rentalArea.closing_hour}
                    onClick={onClick}
                  />
                );
              }
            })}
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}
